/**
 * Utility functions for parsing webhook responses
 */

export interface ParsedWebhookData {
  h1?: string;
  meta?: {
    title?: string;
    description?: string;
    keywords?: string[];
  };
  articleHtml?: string;
  content?: string;
  wordCount?: number;
  readingTime?: number;
  keywords?: string[];
  sources?: any[];
  schemaJsonLd?: any;
  article_type?: string;
  keywordAnalysis?: any;
}

/**
 * Desescapa HTML que viene con escapes JSON
 * Convierte: \"href\" → "href", \n → salto real, etc.
 */
export function unescapeJsonString(str: string): string {
  if (!str || typeof str !== 'string') return str;

  return str
    .replace(/\\"/g, '"')           // \" → "
    .replace(/\\n/g, '\n')          // \n → salto real
    .replace(/\\r/g, '\r')          // \r → return
    .replace(/\\t/g, '\t')          // \t → tab
    .replace(/\\\\/g, '\\')         // \\ → \
    .replace(/\\'/g, "'")           // \' → '
    .replace(/\\&quot;/g, '"')      // \&quot; → "
    .replace(/&quot;/g, '"');       // &quot; → "
}

/**
 * Parse webhook data from the nested structure returned by n8n
 * Structure: [{content: {parts: [{text: "```json\n{...}\n```"}]}}]
 */
export function parseWebhookData(webhookResponse: any): ParsedWebhookData | null {
  try {
    // NUEVO: Handle array format [{ meta, h1, articleHtml, ... }]
    if (Array.isArray(webhookResponse) && webhookResponse.length > 0) {
      const firstItem = webhookResponse[0];

      // Caso 1: NUEVO FORMATO DIRECTO [{ meta, h1, articleHtml, ... }]
      if (firstItem.h1 || firstItem.articleHtml || firstItem.meta) {

        // El HTML ya viene limpio del backend, NO hay que desescapar
        return {
          h1: firstItem.h1 || firstItem.meta?.title || '',
          articleHtml: firstItem.articleHtml || '',
          meta: firstItem.meta || {},
          schemaJsonLd: firstItem.schemaJsonLd || null,
          sources: firstItem.sources || [],
          keywords: firstItem.keywordAnalysis?.primaryKeyword
            ? [
              firstItem.keywordAnalysis.primaryKeyword,
              ...(firstItem.keywordAnalysis.secondaryKeywords || [])
            ].filter(Boolean)
            : [],
          wordCount: firstItem.keywordAnalysis?.totalWords || 0,
          readingTime: Math.ceil((firstItem.keywordAnalysis?.totalWords || 0) / 200) || 0,
          keywordAnalysis: firstItem.keywordAnalysis || null
        } as ParsedWebhookData;
      }

      // Caso 2: FORMATO ANTIGUO content.parts[0].text
      if (firstItem?.content?.parts && Array.isArray(firstItem.content.parts) && firstItem.content.parts[0]?.text) {
        const textContent = firstItem.content.parts[0].text;
        return parseJsonFromMarkdown(textContent);
      }
    }

    // Handle direct object response (formato antiguo)
    if (webhookResponse?.content?.parts && Array.isArray(webhookResponse.content.parts) && webhookResponse.content.parts[0]?.text) {
      const textContent = webhookResponse.content.parts[0].text;
      return parseJsonFromMarkdown(textContent);
    }

    // Handle string response
    if (typeof webhookResponse === 'string') {
      return parseJsonFromMarkdown(webhookResponse);
    }

    // Handle backend wrapper { success: true, article: {...} }
    if (webhookResponse && typeof webhookResponse === 'object' && webhookResponse.success && webhookResponse.article) {
      const art = webhookResponse.article;
      if (art && (art.h1 || art.articleHtml || art.html || art.meta || art.content)) {
        const normalizedHtml = (typeof art.articleHtml === 'string' && art.articleHtml)
          || (typeof art.html === 'string' && art.html)
          || (typeof art.content === 'string' && art.content)
          || '';
        return {
          h1: art.h1 || art.title || art.meta?.title || '',
          articleHtml: normalizedHtml,
          meta: art.meta || {},
          schemaJsonLd: art.schemaJsonLd || null,
          sources: art.sources || [],
          keywords: (art.metadata?.keywords || art.keywords || []) as string[],
          wordCount: art.wordCount || 0,
          readingTime: art.readingTime || 0
        } as ParsedWebhookData;
      }
    }

    // Handle already parsed object (formato antiguo)
    if (webhookResponse && typeof webhookResponse === 'object' && (webhookResponse.h1 || webhookResponse.articleHtml)) {
      return webhookResponse as ParsedWebhookData;
    }

    return null;

  } catch (error) {
    return null;
  }
}

/**
 * Extract and parse JSON from markdown code blocks
 */
function parseJsonFromMarkdown(text: string): ParsedWebhookData | null {
  try {
    // Remove markdown code blocks
    let jsonText = text.trim();

    // Remove ```json and ``` markers
    if (jsonText.includes('```json')) {
      const jsonMatch = jsonText.match(/```json\s*\n([\s\S]*?)\n\s*```/);
      if (jsonMatch) {
        jsonText = jsonMatch[1];
      }
    } else if (jsonText.startsWith('```') && jsonText.endsWith('```')) {
      // Handle generic code blocks
      jsonText = jsonText.replace(/^```[\w]*\s*/gm, '').replace(/\s*```$/gm, '');
    }

    // Parse JSON
    const parsedData = JSON.parse(jsonText);

    // ============================================================
    // LIMPIEZA SIMPLE Y DIRECTA DEL articleHtml
    // ============================================================
    if (parsedData.articleHtml && typeof parsedData.articleHtml === 'string') {
      let html = parsedData.articleHtml;

      // CRÍTICO: Desescapar MÚLTIPLES veces si es necesario
      // Algunas veces el webhook devuelve contenido doblemente escapado

      let previousHtml = '';
      let iterations = 0;
      const maxIterations = 5;

      // Continuar desescapando hasta que no haya cambios
      while (previousHtml !== html && iterations < maxIterations) {
        previousHtml = html;

        // PASO 1: Desescapar JSON básico
        html = html
          .replace(/\\n/g, '\n')
          .replace(/\\r/g, '\r')
          .replace(/\\t/g, '\t')
          .replace(/\\'/g, "'")
          .replace(/\\\\/g, '\\');

        // PASO 2: LIMPIEZA DIRECTA Y SIMPLE DE COMILLAS EN ATRIBUTOS
        // Targeting exact patterns from screenshots
        html = html
          // Fix href="\"...\"" → href="..."
          .replace(/href="\\"/g, 'href="')
          .replace(/\\""/g, '"')
          // Fix rel="\"...\"" → rel="..."  
          .replace(/rel="\\"/g, 'rel="')
          // Fix target="\"...\"" → target="..."
          .replace(/target="\\"/g, 'target="')
          // Fix class="\"...\"" → class="..."
          .replace(/class="\\"/g, 'class="')
          // Fix id="\"...\"" → id="..."
          .replace(/id="\\"/g, 'id="')
          // Clean any remaining \" patterns in attributes
          .replace(/="\\"/g, '="')
          .replace(/\\">/g, '">');

        // PASO 3: Limpiar entidades HTML y \n literales
        html = html
          .replace(/\\&quot;/g, '"')       // \&quot; → "
          .replace(/\&quot;/g, '"')        // &quot; → "
          .replace(/&quot;/g, '"')         // &quot; → "
          .replace(/&#34;/g, '"')          // &#34; → "
          .replace(/&amp;/g, '&')          // &amp; → &
          .replace(/&lt;/g, '<')           // &lt; → <
          .replace(/&gt;/g, '>');          // &gt; → >

        // LIMPIEZA AGRESIVA DE \n LITERALES (que aparecen como texto)
        html = html
          .replace(/\\n/g, '\n')           // \n → salto real (primera pasada)
          .replace(/\\n/g, '\n')           // \n → salto real (segunda pasada)
          .replace(/\\n/g, '\n')           // \n → salto real (tercera pasada)
          .replace(/\\r/g, '\r')           // \r → retorno
          .replace(/\\t/g, '\t');          // \t → tab

        // FINAL: Clean any remaining escaped quotes AFTER attribute cleaning
        html = html.replace(/\\"/g, '"');  // \" → " (final cleanup)

        iterations++;
      }

      // PASO 3.5: CORRECCIÓN ESPECÍFICA PARA ENLACES DEL ÍNDICE (Internal Links)
      // El parser a veces deja target="_blank" en enlaces que empiezan por #
      // Esto rompe la navegación interna. Lo eliminamos robustamente.

      // 1. Remove target="_blank" from any link with href="#"
      html = html.replace(/(<a\s+[^>]*?href="#[^"]*"[^>]*?)target="_blank"([^>]*?>)/gi, '$1$2');
      html = html.replace(/(<a\s+[^>]*?)target="_blank"([^>]*?href="#[^"]*"[^>]*?>)/gi, '$1$2');

      // 2. Remove rel="noopener noreferrer" from internal links too (often paired with target blank)
      html = html.replace(/(<a\s+[^>]*?href="#[^"]*"[^>]*?)rel="noopener noreferrer"([^>]*?>)/gi, '$1$2');
      html = html.replace(/(<a\s+[^>]*?)rel="noopener noreferrer"([^>]*?href="#[^"]*"[^>]*?>)/gi, '$1$2');

      // PASO 4: Eliminar líneas de investigación no deseadas
      html = html
        .replace(/Investigando\s+"[^"]*"\s+━+\s+\(\d+\s+de\s+\d+\)\.{3}/g, '')  // Líneas de investigación
        .replace(/Investigando\s+[^\n]*━+[^\n]*\(\d+\s+de\s+\d+\)[^\n]*/g, '')  // Variante sin comillas
        .replace(/━{10,}/g, '')  // Líneas de guiones largos
        .replace(/\.{6,}/g, '')  // Puntos suspensivos largos
        .replace(/\n\s*\n\s*\n\s*\n+/g, '\n\n')  // Múltiples saltos después de limpieza
        .trim();

      // Limpiar saltos de línea excesivos (pero NO eliminar todos los \n)
      html = html
        .replace(/\n{5,}/g, '\n\n\n')  // Max 3 saltos consecutivos
        .trim();

      parsedData.articleHtml = html;
    }

    // ============================================================
    // VERIFICAR QUE EL H1 ESTÉ PRESENTE
    // ============================================================
    if (parsedData.articleHtml && parsedData.h1) {
      const hasH1 = parsedData.articleHtml.includes('<h1');

      if (!hasH1) {
        // Insertar el h1 después del tag <article> de apertura
        const articleOpenMatch = parsedData.articleHtml.match(/<article[^>]*>/);
        if (articleOpenMatch) {
          const insertPosition = articleOpenMatch.index! + articleOpenMatch[0].length;
          parsedData.articleHtml =
            parsedData.articleHtml.substring(0, insertPosition) +
            `\n<h1>${parsedData.h1}</h1>\n` +
            parsedData.articleHtml.substring(insertPosition);
        }
      }
    }

    // ============================================================
    // INSERTAR SCHEMAS AL FINAL
    // ============================================================
    if (parsedData.schemaJsonLd && parsedData.articleHtml) {

      const schemas = parsedData.schemaJsonLd;
      const scripts = [];

      if (schemas.blogPosting) {
        scripts.push('<script type="application/ld+json">\n' +
          JSON.stringify(schemas.blogPosting, null, 2) +
          '\n</script>');
      }

      if (schemas.faqPage) {
        scripts.push('<script type="application/ld+json">\n' +
          JSON.stringify(schemas.faqPage, null, 2) +
          '\n</script>');
      }

      if (schemas.breadcrumbList) {
        scripts.push('<script type="application/ld+json">\n' +
          JSON.stringify(schemas.breadcrumbList, null, 2) +
          '\n</script>');
      }

      if (scripts.length > 0) {
        const scriptTags = '\n' + scripts.join('\n');
        const closeTag = '</article>';
        const closeIndex = parsedData.articleHtml.lastIndexOf(closeTag);

        if (closeIndex !== -1) {
          parsedData.articleHtml =
            parsedData.articleHtml.substring(0, closeIndex) +
            scriptTags + '\n' +
            parsedData.articleHtml.substring(closeIndex);
        }
      }
    }

    return parsedData as ParsedWebhookData;

  } catch (error) {
    return null;
  }
}

/**
 * Safe render function to prevent React error #31
 */
export function safeRender(value: any): string {
  if (value === null || value === undefined) {
    return '';
  }

  if (typeof value === 'string') {
    return value;
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }

  // If it's an object, stringify it instead of rendering directly
  if (typeof value === 'object') {
    return JSON.stringify(value, null, 2);
  }

  return String(value);
}
